import * as RadioPrimitive from '@radix-ui/react-radio-group';
import { cva, VariantProps } from 'class-variance-authority';
import { useId } from 'react';
import { twMerge } from 'tailwind-merge';
import { WithIcon } from '../../../types/component.types';
import { chip } from '../../Chip/Chip';
import { Text } from '../../Text/Text';
import { TextFieldProps } from '../TextField/TextField';

const singleSelectChip = cva('', {
  variants: {
    intent: {
      default:
        'cursor-pointer rdx-state-unchecked:bg-surface rdx-state-unchecked:hover:bg-hover-opacity rdx-state-checked:bg-primary rdx-state-checked:text-surface rdx-state-checked:hover:bg-primary-hover',
      disabled: 'cursor-not-allowed bg-surface-disabled text-content-disabled',
    },
    width: {
      default: '',
      'fixed-80': 'w-[80px]',
      // The actual flex child of a chip row is the <label> below, not this
      // Item - flex-1/basis have no effect here, so grow/third just fill
      // whatever width the label ends up with (see singleSelectChipLabel).
      grow: 'w-full',
      third: 'w-full',
    },
  },
  defaultVariants: {
    intent: 'default',
    width: 'default',
  },
});

// Sizes the <label> wrapper - the actual flex child within a row of chips -
// rather than the Item it wraps.
const singleSelectChipLabel = cva('', {
  variants: {
    width: {
      default: '',
      'fixed-80': '',
      grow: 'flex-1 min-w-0',
      // Exactly 3 chips per row regardless of container width: basis plus
      // the two gap-xxs gaps between them sums to 100%, so a 4th never fits
      // and the 3rd never wraps early.
      third: 'shrink-0 grow-0 basis-[calc((100%-8px)/3)] min-w-0',
    },
  },
  defaultVariants: {
    width: 'default',
  },
});

const chipText = cva('', {
  variants: {
    textWidth: {
      default: 'max-w-[127px]',
      'fixed-80': 'w-[80px]',
      grow: 'w-full',
      third: 'w-full',
    },
  },
  defaultVariants: {
    textWidth: 'default',
  },
});

type SingleSelectChipProps = RadioPrimitive.RadioGroupItemProps &
  WithIcon &
  VariantProps<typeof chipText> & {
    width?: VariantProps<typeof singleSelectChip>['width'];
    text: string;
    disabled?: boolean;
    // TODO: Implement free text?
    // https://github.com/purposemed/core/issues/11841
    freeText?: TextFieldProps;
  };

/**
 * A single select chip is a radio primitive styled as a chip, used for
 * selecting a single option from a list.
 * - This component should only be used as part of a radio group.
 * - Use `Chip` instead if you need a standalone chip that's not an input.
 * - Chip text should not exceed 20 characters, and if it does, the text will be truncated with an ellipses.
 *
 * @example
 * ```tsx
 * <SingleSelectChip text="My cool option" value="myCoolOption">Click me!</Button>
 * ```
 */
export const SingleSelectChip = ({
  disabled,
  value,
  required,
  text,
  leadingIcon,
  trailingIcon,
  onClick,
  freeText,
  textWidth,
  width,
  ...props
}: SingleSelectChipProps) => {
  const id = useId();
  const intent = disabled ? 'disabled' : 'default';
  // textWidth defaults to whatever width was set, so width="grow" alone
  // grows the text along with the chip - callers only need textWidth when
  // it should diverge from width (rare; none do today).
  const resolvedTextWidth = textWidth ?? width;

  return (
    <label htmlFor={id} className={singleSelectChipLabel({ width })}>
      <RadioPrimitive.Item
        {...props}
        id={id}
        aria-disabled={disabled}
        value={value}
        required={required}
        className={twMerge(chip(), singleSelectChip({ intent, width }))}
        onClick={disabled ? (e) => e.preventDefault() : onClick}
      >
        {leadingIcon && leadingIcon}
        <div className={chipText({ textWidth: resolvedTextWidth })}>
          <Text
            size="bodyS"
            select="none"
            color="contentDefault"
            overflow="truncate"
            title={text}
          >
            {text}
          </Text>
        </div>
        {trailingIcon && trailingIcon}
      </RadioPrimitive.Item>
    </label>
  );
};
